import { useCallback, useEffect, useRef, useState } from 'react';
import { useContent } from '@/hooks/useContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme, darken } from '@mui/material/styles';

const COUNTDOWN_MAX = 60;

interface CountdownRingProps {
  countdown: number;
  accentColor: string;
}

function CountdownRing({ countdown, accentColor }: Readonly<CountdownRingProps>) {
  const circumference = 2 * Math.PI * 22;
  const strokeDashoffset = circumference * (1 - countdown / COUNTDOWN_MAX);
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <Box
        sx={{
          position: 'relative',
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={56}
          height={56}
          viewBox="0 0 56 56"
          fill="none"
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
        >
          <circle cx={28} cy={28} r={22} stroke="#f3f4f6" strokeWidth={4} />
          <circle
            cx={28}
            cy={28}
            r={22}
            stroke={accentColor}
            strokeWidth={4}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.3s' }}
          />
        </svg>
        <Typography
          variant="h5"
          sx={{ fontVariantNumeric: 'tabular-nums', color: accentColor, fontWeight: 'bold' }}
          aria-live="off"
        >
          {countdown}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Idle timeout dialog ─────────────────────────────────────────────────────

export interface IdleTimeoutDialogProps {
  open: boolean;
  countdown: number;
  onExtend: () => void;
  onLogout: () => void;
}

export function IdleTimeoutDialog({
  open,
  countdown,
  onExtend,
  onLogout,
}: Readonly<IdleTimeoutDialogProps>) {
  const { getContent, getInterpolatedContent } = useContent();
  const theme = useTheme();

  const isUrgent = open && countdown <= 15;
  const accentColor = isUrgent ? theme.palette.error.main : theme.palette.primary.main;
  const accentDark = isUrgent
    ? darken(theme.palette.error.main, 0.25)
    : theme.palette.primary.dark;
  const accentDarker = isUrgent
    ? darken(theme.palette.error.main, 0.45)
    : darken(theme.palette.primary.dark, 0.25);

  return (
    <Dialog
      open={open}
      onClose={onExtend}
      aria-labelledby="idle-timeout-title"
      aria-describedby="idle-timeout-desc"
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4, overflow: 'hidden' } } }}
    >
      <Box
        sx={{ height: 4, background: `linear-gradient(90deg, ${accentColor}, ${accentDark})` }}
      />

      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <CountdownRing countdown={countdown} accentColor={accentColor} />

        <Typography id="idle-timeout-title" variant="subtitle1" sx={{ fontWeight: 'bold' }} gutterBottom>
          {getContent('PPSessionTimeoutTitle')}
        </Typography>
        <Typography id="idle-timeout-desc" variant="body2" color="text.secondary">
          {getInterpolatedContent('PPSessionTimeoutMessage', { seconds: String(countdown) })}
        </Typography>

        {/* Screen-reader live region */}
        <Box
          component="span"
          aria-live="polite"
          aria-atomic="true"
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          {getInterpolatedContent('PPSessionCountdownAnnounce', { seconds: String(countdown) })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onExtend}
          autoFocus
          sx={{
            borderRadius: 3,
            py: 1.2,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${accentColor}, ${accentDark})`,
            '&:hover': { background: `linear-gradient(135deg, ${accentDark}, ${accentDarker})` },
          }}
        >
          {getContent('PPSessionContinue')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={onLogout}
          sx={{ borderRadius: 3, py: 1.2, fontWeight: 600, color: 'text.secondary' }}
        >
          {getContent('PortalLogout')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Self-contained wrapper ─────────────────────────────────────────────────
//
// SessionTimeoutModal manages its own idle timer and countdown so it can be
// dropped in without wiring useScreenSession. The idle period and countdown
// duration are kept here so the test file can assert against the same values.

export const SESSION_INACTIVITY_MS = 14 * 60 * 1000; // 14 min
export const SESSION_COUNTDOWN_S = 60;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll',
];

export interface SessionTimeoutModalProps {
  onLogout: () => void;
}

export function SessionTimeoutModal({ onLogout }: Readonly<SessionTimeoutModalProps>) {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(SESSION_COUNTDOWN_S);

  const openRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    clearCountdown();
    setCountdown(SESSION_COUNTDOWN_S);
    let remaining = SESSION_COUNTDOWN_S;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownRef.current!);
        countdownRef.current = null;
        setCountdown(0);
        setOpen(false);
        openRef.current = false;
        onLogout();
      } else {
        setCountdown(remaining);
      }
    }, 1000);
  }, [clearCountdown, onLogout]);

  const startIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      openRef.current = true;
      setOpen(true);
      startCountdown();
    }, SESSION_INACTIVITY_MS);
  }, [startCountdown]);

  const handleExtend = useCallback(() => {
    clearCountdown();
    openRef.current = false;
    setOpen(false);
    setCountdown(SESSION_COUNTDOWN_S);
    startIdleTimer();
  }, [clearCountdown, startIdleTimer]);

  const handleLogout = useCallback(() => {
    clearCountdown();
    openRef.current = false;
    setOpen(false);
    onLogout();
  }, [clearCountdown, onLogout]);

  const resetIdle = useCallback(() => {
    if (openRef.current) return;
    startIdleTimer();
  }, [startIdleTimer]);

  useEffect(() => {
    startIdleTimer();
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, resetIdle, { passive: true }));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      clearCountdown();
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, resetIdle));
    };
  }, [startIdleTimer, resetIdle, clearCountdown]);

  return (
    <IdleTimeoutDialog
      open={open}
      countdown={countdown}
      onExtend={handleExtend}
      onLogout={handleLogout}
    />
  );
}

// ── Absolute timeout dialog ─────────────────────────────────────────────────

export interface AbsoluteTimeoutDialogProps {
  open: boolean;
  countdown: number;
  onLogout: () => void;
}

export function AbsoluteTimeoutDialog({
  open,
  countdown,
  onLogout,
}: Readonly<AbsoluteTimeoutDialogProps>) {
  const { getContent, getInterpolatedContent } = useContent();
  const theme = useTheme();

  const isUrgent = open && countdown <= 15;
  const accentColor = isUrgent ? theme.palette.error.main : theme.palette.primary.main;
  const accentDark = isUrgent
    ? darken(theme.palette.error.main, 0.25)
    : theme.palette.primary.dark;
  const accentDarker = isUrgent
    ? darken(theme.palette.error.main, 0.45)
    : darken(theme.palette.primary.dark, 0.25);

  return (
    <Dialog
      open={open}
      onClose={() => undefined}
      aria-labelledby="absolute-timeout-title"
      aria-describedby="absolute-timeout-desc"
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4, overflow: 'hidden' } } }}
    >
      <Box
        sx={{ height: 4, background: `linear-gradient(90deg, ${accentColor}, ${accentDark})` }}
      />

      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <CountdownRing countdown={countdown} accentColor={accentColor} />

        <Typography id="absolute-timeout-title" variant="subtitle1" sx={{ fontWeight: 'bold' }} gutterBottom>
          {getContent('PPSessionTimeoutTitle')}
        </Typography>
        <Typography id="absolute-timeout-desc" variant="body2" color="text.secondary">
          {getInterpolatedContent('PPSessionTimeoutMessage', { seconds: String(countdown) })}
        </Typography>

        {/* Screen-reader live region */}
        <Box
          component="span"
          aria-live="polite"
          aria-atomic="true"
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          {getInterpolatedContent('PPSessionCountdownAnnounce', { seconds: String(countdown) })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onLogout}
          autoFocus
          sx={{
            borderRadius: 3,
            py: 1.2,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${accentColor}, ${accentDark})`,
            '&:hover': { background: `linear-gradient(135deg, ${accentDark}, ${accentDarker})` },
          }}
        >
          {getContent('PortalOk')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
